// iter-121.116 Phase 3 — "For Your Action" lane for SA Home.
//
// Single home-widget that replaces the scattered `tenant_health` /
// `billing_health` / `datasets_attention` action signals. Fetches the
// 6-count summary from `/api/home/cockpit/actions` and renders:
//   • An "All clear" hero card when every count is zero
//   • Otherwise a responsive grid of action tiles, each opening a Sheet
//     drawer with row-level detail.
//
// Hierarchy reminder: all aggregations roll up to Org. The drawers show
// Org name primary, Tenant name only when an Org has > 1 tenant.
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCockpitScope } from "@/lib/cockpit-scope";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle, Flame, Plug, Activity, Mail, UserX, CheckCircle2, ArrowUpRight, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ACTION_META = {
    trials_expiring_7d:      { Icon: Flame,          tone: "warn" },
    orgs_over_budget:        { Icon: AlertTriangle,  tone: "warn" },
    connectors_failing_24h:  { Icon: Plug,           tone: "critical" },
    slow_queries_today:      { Icon: Activity,       tone: "info" },
    email_templates_pending: { Icon: Mail,           tone: "info" },
    signups_stalled_24h:     { Icon: UserX,          tone: "warn" },
};

function toneClasses(tone) {
    switch (tone) {
        case "critical":
            return {
                ring: "border-destructive/40",
                bg: "bg-destructive/5 hover:bg-destructive/10",
                icon: "text-destructive",
                badge: "bg-destructive/15 text-destructive border-destructive/30",
            };
        case "warn":
            return {
                ring: "border-amber-500/40",
                bg: "bg-amber-500/5 hover:bg-amber-500/10",
                icon: "text-amber-600 dark:text-amber-400",
                badge: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
            };
        default:
            return {
                ring: "border-border",
                bg: "bg-secondary/40 hover:bg-secondary/60",
                icon: "text-foreground",
                badge: "bg-secondary text-foreground border-border",
            };
    }
}

function AllClearCard({ onAcknowledge }) {
    return (
        <div className="border border-emerald-500/30 rounded-sm bg-emerald-500/5 p-5 flex items-center gap-4"
             data-testid="sa-action-lane-all-clear">
            <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400 shrink-0" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-emerald-700 dark:text-emerald-300">All clear</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                    Nothing needs your attention in the selected scope. Sit back — or open the Information lane below to review platform health.
                </div>
            </div>
            <Button variant="outline" size="sm" className="rounded-sm h-8 shrink-0"
                    onClick={onAcknowledge}
                    data-testid="sa-action-lane-refresh">
                Refresh
            </Button>
        </div>
    );
}

function ActionTile({ item, onOpen }) {
    const meta = ACTION_META[item.key] || ACTION_META.slow_queries_today;
    const cls = toneClasses(meta.tone);
    const { Icon } = meta;
    return (
        <button
            type="button"
            onClick={() => onOpen(item)}
            className={`text-left border ${cls.ring} ${cls.bg} rounded-sm p-3 flex flex-col gap-2 transition-colors`}
            data-testid={`sa-action-tile-${item.key}`}
        >
            <div className="flex items-start justify-between gap-2">
                <Icon className={`h-5 w-5 ${cls.icon}`} strokeWidth={1.5} />
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex items-baseline justify-between gap-2">
                <span className="kpi-number text-2xl leading-none font-semibold">
                    {item.count}
                </span>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-wider ${cls.badge}`}>
                    {item.severity}
                </span>
            </div>
            <div className="text-[12px] font-medium leading-tight">{item.label}</div>
            <div className="text-[11px] text-muted-foreground leading-snug line-clamp-2">{item.hint}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-primary mt-1 inline-flex items-center gap-1">
                {item.cta_label} <ArrowUpRight className="h-3 w-3" />
            </div>
        </button>
    );
}

// Lazy import of drawer content registry — split out so we can keep this
// file under the 50-line target per logical block.
import ActionDrawer from "./ActionDrawer";

export default function SaActionLane({ onEmpty }) {
    const { queryString } = useCockpitScope();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null); // selected item or null
    const navigate = useNavigate();

    const fetchData = useCallback(async () => {
        try {
            const r = await api.get(`/home/cockpit/actions?${queryString}`);
            setData(r.data || null);
        } catch {
            setData({ items: [], total_action_count: 0, all_clear: true });
        }
    }, [queryString]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Self-report empty so the Home grid can compact rows in non-edit mode.
    // We do NOT self-report empty when `all_clear` — we want the "All clear"
    // card visible so the SA gets positive feedback.
    useEffect(() => {
        if (onEmpty && data && !data.all_clear) onEmpty(false);
    }, [data, onEmpty]);

    if (data === null) {
        return (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
                 data-testid="sa-action-lane-loading">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="h-24 shimmer rounded-sm" />
                ))}
            </div>
        );
    }

    if (data.all_clear) {
        return (
            <div data-testid="sa-action-lane">
                <div className="eyebrow text-[10px] mb-2">For Your Action</div>
                <AllClearCard onAcknowledge={fetchData} />
            </div>
        );
    }

    // Sort: keep declared order but push count==0 to the back, then by count desc.
    const visible = data.items.filter((it) => (it.count || 0) > 0);
    const dimmed = data.items.filter((it) => (it.count || 0) === 0);

    return (
        <div data-testid="sa-action-lane">
            <div className="flex items-center justify-between mb-2">
                <div className="eyebrow text-[10px]">For Your Action</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {data.total_action_count} {data.total_action_count === 1 ? "signal" : "signals"}
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {visible.map((it) => (
                    <ActionTile key={it.key} item={it} onOpen={setOpen} />
                ))}
                {dimmed.length > 0 && visible.length < 6 && dimmed.slice(0, 6 - visible.length).map((it) => (
                    <div key={it.key}
                         className="border border-dashed border-border rounded-sm p-3 flex flex-col gap-1 opacity-60"
                         data-testid={`sa-action-tile-zero-${it.key}`}>
                        <div className="text-[11px] text-muted-foreground line-clamp-1">{it.label}</div>
                        <div className="kpi-number text-xl text-muted-foreground">0</div>
                    </div>
                ))}
            </div>

            <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
                <SheetContent className="sm:max-w-xl w-full" data-testid="sa-action-drawer">
                    {open && (
                        <>
                            <SheetHeader>
                                <SheetTitle>{open.label}</SheetTitle>
                                <SheetDescription>{open.hint}</SheetDescription>
                            </SheetHeader>
                            <div className="mt-4">
                                <ActionDrawer item={open} onClose={() => setOpen(null)} onNavigate={(to) => { setOpen(null); navigate(to); }} />
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}
